import type { ScheduleOverrides } from '@features/quote/types/quote.types';
import { isDateKey, type DateKey } from '@features/quote/utils/dateKey';
import { derivationContextChanged, type DerivationContext } from './derivationFingerprint';

export type ScheduleOverridesVerdict =
    | { usable: true }
    | { usable: false; scope: 'document'; reason: 'fingerprint' | 'malformed' }
    | { usable: false; scope: 'dates'; reason: 'expired' };

function isWellFormed(overrides: ScheduleOverrides): boolean {
    const { startEntryIndex } = overrides;
    const validStart =
        startEntryIndex === null || (Number.isInteger(startEntryIndex) && startEntryIndex >= 0);
    if (!validStart) {
        return false;
    }
    return Object.entries(overrides.dateOverrides ?? {}).every(([key, value]) => {
        const row = Number(key);
        return Number.isInteger(row) && row >= 0 && isDateKey(value);
    });
}

// Staleness, unreadability, and calendar expiry collapse into one verdict for
// the discard-and-toast effect, evaluated once the derive settles.
export function verifyScheduleOverrides(
    overrides: ScheduleOverrides | undefined,
    currentFingerprint: string,
    today: DateKey
): ScheduleOverridesVerdict {
    if (!overrides) {
        return { usable: true };
    }
    if (!isWellFormed(overrides)) {
        return { usable: false, scope: 'document', reason: 'malformed' };
    }
    if (overrides.derivationFingerprint !== currentFingerprint) {
        return { usable: false, scope: 'document', reason: 'fingerprint' };
    }
    const hasExpired = Object.values(overrides.dateOverrides ?? {}).some((date) => date < today);
    if (hasExpired) {
        return { usable: false, scope: 'dates', reason: 'expired' };
    }
    return { usable: true };
}

// Runs before the request is built rather than as a settled follow-up, so an
// unreadable, expired, or stale-context date never reaches the server.
export function vetOverrideDatesForRequest(
    overrides: ScheduleOverrides | undefined,
    today: DateKey,
    context: DerivationContext
): Record<number, DateKey> | undefined {
    if (!overrides) {
        return undefined;
    }
    if (!isWellFormed(overrides)) {
        return {};
    }
    if (derivationContextChanged(overrides.derivationFingerprint, context)) {
        return {};
    }
    const hasExpired = Object.values(overrides.dateOverrides ?? {}).some((date) => date < today);
    return hasExpired ? {} : overrides.dateOverrides;
}

// A well-formed key beyond the number of rows the derive emits is inert rather
// than invalid; a malformed key is left for `verifyScheduleOverrides` to reject.
export function clampScheduleOverrideRows(
    overrides: ScheduleOverrides,
    serviceCount: number
): ScheduleOverrides {
    const isInertRow = (key: string): boolean => {
        const row = Number(key);
        return Number.isInteger(row) && row >= 0 && row >= serviceCount;
    };
    const entries = Object.entries(overrides.dateOverrides ?? {});
    const kept = entries.filter(([key]) => !isInertRow(key));
    if (kept.length === entries.length) {
        return overrides;
    }
    return { ...overrides, dateOverrides: Object.fromEntries(kept) };
}
